import ConfigBusiness from '../../commons/config/ConfigBusiness.js';

/**
 * Declaración de los filtros de la grilla de Móviles para <GridFilterPanel />.
 *
 * Vive en su propio módulo porque lo consumen DOS lados: el panel (para renderizar) y
 * MovilesGrid (para calcular el query por defecto con las mismas reglas de serialización).
 *
 * VEC-3690 fase 1: migración al panel compartido, conservando el tipo de control de cada
 * campo y el query EXACTO que armaba `MovilesGridFiltros::queryTable()`.
 * VEC-3690 fase 2: los 11 desplegables simples pasaron a `multiselect`.
 *
 * El query NO cambió de forma: un multiselect con un solo valor serializa igual que el select
 * —`base=7`—, y del lado del backend el filtro parte ese valor por coma y busca con `whereIn`,
 * que con un elemento es la misma consulta que la igualdad de antes. Eso es lo que mantiene
 * funcionando a los consumidores que NO son esta grilla.
 *
 * DOS CLAVES DEL QUERY NO SON CAMPOS DEL PANEL y las agrega MovilesGrid:
 *  - `filtroKpi`: no tiene control, sale de las tarjetas de KPI (props.kpi / location.state).
 *  - `medidor`: quedó sin control en el panel viejo pero igual viajaba. Se sigue mandando
 *    vacía para que el query no cambie de forma; limpiarla es tema de otra card.
 */

export const MOVILES_FILTROS_STORAGE_KEY = 'MovilesGrid_filtros';

/**
 * La grilla de Móviles abre SIN filtros aplicados: no hay defaults, a diferencia de Tickets
 * (PENDIENTES) o Facturas (último mes). Se expone igual como función para que la grilla y el
 * panel usen la misma fuente, y para que "Limpiar Filtros" tenga a dónde volver.
 */
export const getMovilesFilterDefaults = () => ({});

const ANCHO_MEDIO = { xs: 12, sm: 6, md: 3, lg: 3 };
const ANCHO_CHICO = { xs: 12, sm: 6, md: 2, lg: 2 };

/**
 * El dominio tiene DOS formas según `servicios.enableMultiSearch`: múltiple con opciones, o
 * texto libre. Se decide acá, al declarar el campo, porque los dos tipos ya existen en el
 * panel: no hace falta ningún control nuevo ni tocar nada compartido.
 *
 * Se conserva la bifurcación a propósito (decisión del usuario). En Tickets, VEC-3555
 * eliminó esta misma config unificando en `pastableMultiselect`; si acá se quiere lo mismo,
 * va en otra card.
 */
const tipoDeDominio = () =>
	ConfigBusiness.get('servicios.enableMultiSearch') === 'true' ? 'multiselect' : 'text';

/**
 * "Medidor hasta" menor que "Medidor desde" muestra "Valores incorrectos".
 *
 * Se replica la condición del panel viejo TAL CUAL, incluida su rareza: compara con
 * `Math.min`, que castea los strings a número. Y es SOLO UN CARTEL — no deshabilita el
 * botón Buscar, se puede buscar igual con el rango invertido. Cambiar eso sería otra
 * decisión de producto.
 */
const validarMedidorHasta = (valor, estado) => {
	const desde = estado && estado.medidorDesde !== undefined ? estado.medidorDesde : '';
	const hasta = valor === undefined || valor === null ? '' : valor;
	if (hasta === '' || desde === hasta) return null;
	return Math.min(desde, hasta) === Number(hasta) ? 'Valores incorrectos' : null;
};

/**
 * @param columnsEnabled  los flags de config que ya calcula MovilesGrid::configColumns
 *
 * Sobre `visible`: 21 campos se gatean por flag, pero en el panel viejo NO todos se ocultaban.
 * 18 estaban escritos como `{...!columnsEnabled.paisEnabled && { display: "none" }}`, que pasa
 * `display` como PROP de Grid y en MUI v4 no hace nada: esos 18 se mostraban SIEMPRE, con el
 * flag apagado también. Acá el gateo pasa a ser efectivo, que es lo que el código pretendía, y
 * ese es el único cambio observable de esta migración (mismo caso que VEC-3698 en Tickets).
 * Los otros 3 (responsables2, supervisores, temporales) usaban render condicional
 * `{flag && (...)}`, que sí funcionaba: para esos no cambia nada.
 */
export const buildMovilesFilterFields = (columnsEnabled) => {
	const columnas = columnsEnabled || {};

	return [
		{
			name: 'dominio',
			type: tipoDeDominio(),
			messageId: 'movilesGrid.render.filtros_dominio.label',
			label: 'Dominio',
			size: ANCHO_MEDIO,
		},
		{
			name: 'marca',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_marca.label',
			label: 'Marca',
			size: ANCHO_MEDIO,
		},
		{
			name: 'modelo',
			type: 'text',
			messageId: 'movilesGrid.render.filtros_modelo.label',
			label: 'Modelo',
			size: ANCHO_MEDIO,
		},
		{
			name: 'tipo',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_tipo.label',
			label: 'Tipo',
			size: ANCHO_MEDIO,
		},
		{
			name: 'estado',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_estados.label',
			label: 'Estados',
			size: ANCHO_MEDIO,
		},
		{
			name: 'unidad',
			type: 'text',
			messageId: 'movilesGrid.render.filtros_unidad.label',
			label: 'Unidad',
			size: ANCHO_MEDIO,
			visible: columnas.unidadEnabled === true,
		},
		{
			name: 'chasis',
			type: 'text',
			messageId: 'movilesGrid.render.filtros_chasis.label',
			label: 'Chasis',
			size: ANCHO_MEDIO,
			visible: columnas.chasisEnabled === true,
		},
		{
			name: 'pais',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_pais.label',
			label: 'País',
			size: ANCHO_MEDIO,
			visible: columnas.paisEnabled === true,
		},
		{
			name: 'region',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_region.label',
			label: 'Región',
			size: ANCHO_MEDIO,
			visible: columnas.regionEnabled === true,
		},
		{
			name: 'subRegion',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_subRegion.label',
			label: 'Sub.Región',
			size: ANCHO_MEDIO,
			visible: columnas.subRegionEnabled === true,
		},
		{
			name: 'base',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_base.label',
			label: 'Base',
			size: ANCHO_MEDIO,
			visible: columnas.baseEnabled === true,
		},
		{
			name: 'titular',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_titular.label',
			label: 'Titular',
			size: ANCHO_MEDIO,
			visible: columnas.titularEnabled === true,
		},
		{
			name: 'companiaOrigen',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_companiaOrigen.label',
			label: 'Cía. Origen',
			size: ANCHO_MEDIO,
			visible: columnas.companiaOrigenEnabled === true,
		},
		{
			name: 'centroCosto',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_centroCosto.label',
			label: 'Centro de Costos',
			size: ANCHO_MEDIO,
			visible: columnas.centroCostosEnabled === true,
		},
		{
			name: 'cecosDireccion',
			type: 'text',
			messageId: 'movilesGrid.render.filtros_centroCosto_direccion.label',
			label: 'CC.Dirección',
			size: ANCHO_MEDIO,
			visible: columnas.cecosDireccionEnabled === true,
		},
		{
			name: 'cecosMercado',
			type: 'text',
			messageId: 'movilesGrid.render.filtros_centroCosto_mercado.label',
			label: 'CC.Mercado',
			size: ANCHO_MEDIO,
			visible: columnas.cecosMercadoEnabled === true,
		},
		{
			name: 'cecosGerencia',
			type: 'text',
			messageId: 'movilesGrid.render.filtros_centroCosto_gerencia.label',
			label: 'CC.Gerencia',
			size: ANCHO_MEDIO,
			visible: columnas.cecosGerenciaEnabled === true,
		},
		{
			name: 'cecosArea',
			type: 'text',
			messageId: 'movilesGrid.render.filtros_centroCosto_area.label',
			label: 'CC.Área',
			size: ANCHO_MEDIO,
			visible: columnas.cecosAreaEnabled === true,
		},
		{
			name: 'responsable1',
			type: 'text',
			messageId: 'movilesGrid.render.filtros_responsable1.label',
			label: 'Responsable 1',
			size: ANCHO_MEDIO,
			visible: columnas.responsable1Enabled === true,
		},
		{
			// OJO: en el panel viejo el state guardaba solo los IDS (`value.map(v => v.id)`),
			// no los objetos. El panel guarda {value,label}, así que las opciones tienen que
			// venir con `value` = el id para que el query salga igual.
			name: 'responsables2',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_responsable2.label',
			label: 'Responsable 2',
			size: ANCHO_MEDIO,
			visible: columnas.responsable2Enabled === true,
		},
		{
			// Mismo caso que responsables2: el state viejo eran ids sueltos.
			name: 'supervisores',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_supervisor.label',
			label: 'Supervisor',
			size: ANCHO_MEDIO,
			visible: columnas.supervisorEnabled === true,
		},
		{
			// Mismo caso que responsables2: el state viejo eran ids sueltos.
			name: 'temporales',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_temporal.label',
			label: 'Temporal',
			size: ANCHO_MEDIO,
			visible: columnas.temporalEnabled === true,
		},
		{
			name: 'rutas',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_rutas.label',
			label: 'Rutas',
			size: ANCHO_MEDIO,
			visible: columnas.rutaEnabled === true,
		},
		{
			// OJO: el panel viejo serializaba `.id` acá y `.value` en los demás múltiples.
			// Las opciones tienen que construirse con `value` = el id.
			name: 'transportadora',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_transportadora.label',
			label: 'Transportadora',
			size: ANCHO_MEDIO,
			visible: columnas.transportadoraEnabled === true,
		},
		{
			// Mismo caso que transportadora: serializaba `.id`. Y el flag se llama
			// `rentingEnabled`, no `rentadoraEnabled`.
			name: 'rentadora',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_rentadora.label',
			label: 'Rentadora',
			size: ANCHO_MEDIO,
			visible: columnas.rentingEnabled === true,
		},
		{
			name: 'funcion',
			type: 'multiselect',
			messageId: 'movilesGrid.render.filtros_funcion.label',
			label: 'Función',
			size: ANCHO_MEDIO,
		},
		{
			name: 'esReserva',
			type: 'multiselect',
			messageId: 'MovilesGridFiltros.render.filtros_es_reserva.label',
			label: '¿Es reserva?',
			size: ANCHO_MEDIO,
			visible: columnas.esReservaEnabled === true,
		},
		{
			// *** GATEADO POR CONFIG, Y NO POR `columnsEnabled` ***: no tiene columna en la grilla,
			// así que su flag no pasa por configColumns. En el panel viejo el bloque entero estaba
			// envuelto en `{ConfigBusiness.get(...) === 'true' && (...)}`, y al migrar quedó sin
			// gate: el filtro aparecía en instancias donde la vinculación de activos está apagada.
			// El backend igual lo ignora con el flag en false (MovilesService::queryFilters), así
			// que sin esto se ofrece un filtro que no filtra.
			name: 'estadoVinculacion',
			type: 'multiselect',
			messageId: 'MovilesGridFiltros.render.filtros_estadoVinculacion.label',
			label: 'Estado de vinculación',
			size: ANCHO_MEDIO,
			visible: ConfigBusiness.get('moviles.vinculacionActivos.habilitado') === 'true',
		},
		{
			// `inputProps` se pasa tal cual al TextField: los dos campos del medidor eran
			// type="number" con min 0 y hay que conservarlo (sin esto el input acepta letras).
			name: 'medidorDesde',
			type: 'text',
			messageId: 'Medidor_desde',
			label: 'Medidor desde',
			size: ANCHO_CHICO,
			inputProps: { type: 'number', min: 0 },
		},
		{
			name: 'medidorHasta',
			type: 'text',
			messageId: 'Medidor_hasta',
			label: 'Medidor hasta',
			size: ANCHO_CHICO,
			inputProps: { type: 'number', min: 0 },
			validate: validarMedidorHasta,
		},
		{
			// El `check` serializa '1'/'' y el panel viejo mandaba `true`/''. Verificado en
			// MovilesService.php:889: filtra a activos cuando el valor es vacío o 'false', así
			// que '1' entra por la misma rama que 'true'. El query cambia de texto, el
			// resultado no.
			name: 'showInactivos',
			type: 'check',
			messageId: 'formulariosGridFilters.render.filtros.ver_inactivos',
			label: 'Ver Inactivos',
		},
	];
};
